import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Newspaper, Rocket, Star, Radar, Search, User, Menu, X, Users, History, BarChart3, Shield, Radio } from "lucide-react";

const NAV = [
  { to: "/news", label: "NEWS", icon: Newspaper, testid: "nav-news" },
  { to: "/launches", label: "LAUNCHES", icon: Rocket, testid: "nav-launches" },
  { to: "/history", label: "HISTORY", icon: History, testid: "nav-history" },
  { to: "/events", label: "EVENTS", icon: Star, testid: "nav-events" },
  { to: "/fleet", label: "FLEET", icon: Radar, testid: "nav-fleet" },
  { to: "/roster", label: "ROSTER", icon: Users, testid: "nav-roster" },
  { to: "/community", label: "COMMUNITY", icon: Radio, testid: "nav-community" },
  { to: "/stats", label: "STATS", icon: BarChart3, testid: "nav-stats" },
  { to: "/scan", label: "SCAN", icon: Search, testid: "nav-scan" },
  { to: "/pilot", label: "PILOT", icon: User, testid: "nav-pilot" },
];

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  const utc = t.toISOString().slice(11, 19);
  return <span className="font-mono text-[11px] tracking-wider text-[color:var(--sat-text-dim)]" data-testid="nav-clock">{utc} UTC</span>;
}

export default function Nav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-[color:var(--sat-border)] bg-black/85 backdrop-blur-md">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-14 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/news" className="flex items-center gap-3 group" data-testid="nav-logo">
          <svg
            viewBox="0 0 32 32"
            className="w-8 h-8 sat-cyan sat-glow group-hover:rotate-[8deg] transition-transform"
            fill="none"
            aria-hidden="true"
          >
            <ellipse cx="16" cy="16" rx="14" ry="5" stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
            <ellipse cx="16" cy="16" rx="14" ry="5" stroke="currentColor" strokeWidth="1.2" transform="rotate(60 16 16)" opacity="0.4" />
            <ellipse cx="16" cy="16" rx="14" ry="5" stroke="currentColor" strokeWidth="1.2" transform="rotate(-60 16 16)" opacity="0.4" />
            <circle cx="16" cy="16" r="4" fill="currentColor" />
            <circle cx="28" cy="12" r="1.5" fill="currentColor" opacity="0.9" />
          </svg>
          <div className="leading-none">
            <div className="font-display font-bold text-lg text-[color:var(--sat-text)] tracking-widest">ASTRONATLAS</div>
            <div className="sat-label text-[8px] mt-0.5 text-[color:var(--sat-cyan)]">v2.0 // WEB</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-[11px] font-mono tracking-widest border transition-all ${
                  isActive
                    ? "border-[color:var(--sat-cyan)] text-[color:var(--sat-cyan)] bg-[color:var(--sat-cyan-soft)]"
                    : "border-transparent text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] hover:border-[color:var(--sat-border-bright)]"
                }`
              }>
              <Icon size={13} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-4">
          <Clock />
          <div className="flex items-center gap-2 border border-[color:var(--sat-live)] px-2 py-1">
            <span className="sat-live-dot" />
            <span className="font-mono text-[10px] tracking-widest text-[color:var(--sat-live)]">LIVE</span>
          </div>
          {user ? (
            <>
              {user.is_admin && (
                <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1 border border-[color:var(--sat-cyan)] px-2 py-1 sat-cyan font-mono text-[10px] tracking-widest hover:bg-[color:var(--sat-cyan-soft)]">
                  <Shield size={11} /> ADMIN
                </Link>
              )}
              <Link to="/pilot" className="font-mono text-[11px] text-[color:var(--sat-cyan)] tracking-wider" data-testid="nav-user-callsign">
                @{user.call_sign}
              </Link>
            </>
          ) : (
            <Link to="/auth" data-testid="nav-auth" className="sat-btn !py-2 !px-3">AUTHENTICATE</Link>
          )}
        </div>

        {/* Mobile */}
        <button className="lg:hidden text-[color:var(--sat-cyan)]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-[color:var(--sat-border)] bg-black">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-xs font-mono tracking-widest border-b border-[color:var(--sat-border)] ${
                  isActive ? "text-[color:var(--sat-cyan)] bg-[color:var(--sat-cyan-soft)]" : "text-[color:var(--sat-text-dim)]"
                }`
              }>
              <Icon size={14} /> {label}
            </NavLink>
          ))}
          {!user && (
            <Link to="/auth" data-testid="nav-auth-mobile" className="block px-6 py-3 text-xs font-mono tracking-widest text-[color:var(--sat-cyan)]">
              AUTHENTICATE →
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
